import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent } from 'react';
import { Check, KeyRound, ShieldCheck } from 'lucide-react';
import { SiteAccessError, unlockSite } from '../siteAccess';
import { prefersReducedMotion } from '../browserCompat';

type Phase = 'idle' | 'checking' | 'orbit' | 'verified' | 'error';

const emptyCode = () => Array.from({ length: 6 }, () => '');

export function SiteAccessGate({ onUnlock }: { onUnlock: () => void }) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const [characters, setCharacters] = useState(emptyCode);
  const [phase, setPhase] = useState<Phase>('idle');
  const [message, setMessage] = useState('');
  const reduced = useMemo(prefersReducedMotion, []);
  const code = characters.join('');

  useEffect(() => { inputs.current[0]?.focus(); }, []);
  useEffect(() => {
    if (code.length !== 6 || phase !== 'idle') return;
    const timer = setTimeout(() => void verify(code), 90);
    return () => clearTimeout(timer);
  }, [code, phase]);

  async function verify(value = code) {
    if (!/^[A-Z0-9]{6}$/.test(value) || phase !== 'idle') return;
    setPhase('checking'); setMessage('Verifying secure website code…');
    try {
      await unlockSite(value);
      setPhase('orbit'); setMessage('Code accepted. Forming secure orbit…');
      const verifyDelay = reduced ? 120 : 1_100;
      const exitDelay = reduced ? 420 : 1_900;
      setTimeout(() => { setPhase('verified'); setMessage('Website access verified'); }, verifyDelay);
      setTimeout(onUnlock, exitDelay);
    } catch (problem) {
      setPhase('error');
      setMessage(problem instanceof SiteAccessError ? problem.message : 'The website gate could not be reached.');
      setTimeout(() => {
        setCharacters(emptyCode()); setPhase('idle');
        requestAnimationFrame(() => inputs.current[0]?.focus());
      }, reduced ? 250 : 650);
    }
  }

  function update(index: number, raw: string) {
    if (phase !== 'idle') return;
    const nextCharacter = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(-1);
    setCharacters((current) => current.map((character, position) => position === index ? nextCharacter : character));
    if (nextCharacter && index < 5) requestAnimationFrame(() => inputs.current[index + 1]?.focus());
  }

  function key(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && !characters[index] && index > 0) {
      setCharacters((current) => current.map((character, position) => position === index - 1 ? '' : character));
      requestAnimationFrame(() => inputs.current[index - 1]?.focus());
    }
    if (event.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    if (event.key === 'ArrowRight' && index < 5) inputs.current[index + 1]?.focus();
  }

  function paste(raw: string) {
    const next = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6).split('');
    if (!next.length || phase !== 'idle') return;
    setCharacters(Array.from({ length: 6 }, (_, index) => next[index] ?? ''));
    requestAnimationFrame(() => inputs.current[Math.min(next.length, 6) - 1]?.focus());
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void verify();
  }

  return <main className={`site-access-gate phase-${phase}`}>
    <div className="site-access-grid" aria-hidden="true" />
    <div className="site-access-glow" aria-hidden="true" />
    <section className="site-access-card" aria-labelledby="site-access-title">
      <header className="site-access-brand">
        <img src="/brand/crest-384.png" alt="DISMUN 2026" />
        <div><span>DISMUN 2026</span><strong>EXECUTIVE BOARD DESKTOP</strong></div>
      </header>
      <div className="site-access-copy">
        <p className="eyebrow">SECURE WEBSITE GATEWAY</p>
        <h1 id="site-access-title">Enter access code</h1>
        <p>Enter the six-character website code. Your separate EB access code will be requested after this gateway opens.</p>
      </div>
      <form onSubmit={submit}>
        <div className="site-code-stage">
          <div className="site-code-orbit" aria-hidden="true"><i /><i /><i /></div>
          <div className="site-code-fields" onPaste={(event) => { event.preventDefault(); paste(event.clipboardData.getData('text')); }}>
            {characters.map((character, index) => {
              const initialX = (index - 2.5) * 58;
              const angle = -90 + index * 60;
              const radians = angle * Math.PI / 180;
              const targetX = Math.cos(radians) * 72;
              const targetY = Math.sin(radians) * 72;
              const style = {
                '--row-x': `${initialX}px`,
                '--orbit-x': `${targetX}px`,
                '--orbit-y': `${targetY}px`,
                '--orbit-angle': `${angle + 90}deg`,
                '--code-delay': `${index * 34}ms`,
              } as CSSProperties;
              return <input
                ref={(node) => { inputs.current[index] = node; }}
                key={index}
                style={style}
                className="site-code-cell"
                aria-label={`Website access code character ${index + 1}`}
                inputMode="text"
                autoCapitalize="characters"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={character}
                disabled={phase !== 'idle'}
                onChange={(event) => update(index, event.target.value)}
                onKeyDown={(event) => key(index, event)}
              />;
            })}
          </div>
          <div className="site-code-confirm" aria-hidden="true"><Check /></div>
        </div>
        <div className="site-access-status" aria-live="polite">
          {phase === 'idle' ? <><KeyRound /> Six letters or numbers</> : phase === 'verified' ? <><ShieldCheck /> {message}</> : message}
        </div>
        {phase === 'idle' && <button type="submit" className="site-access-submit" disabled={code.length !== 6}>Verify website code</button>}
      </form>
      <footer><ShieldCheck /> Server verified · Rate limited · Secure browser session</footer>
    </section>
  </main>;
}
